// When a catering order can be fulfilled. Ported from corporate-accounts-platform's
// composables/cateringSchedule.ts: the same notice and 3-month rules. Corporate offers
// hourly 7 AM - 6 PM slots; this app offers the whole day in 15-minute steps and lets
// the venue's opening hours narrow them. Corporate resolves times in Sydney because
// its request is set before a venue is picked; here the menu page always has a
// venue, so its timezone is used instead.
import moment from 'moment-timezone'

export const DEFAULT_CATERING_TIMEZONE = 'Australia/Sydney'

/** The earliest slot is the one nearest to this long after the order is placed. */
export const MIN_NOTICE_HOURS = 24

/** An order can be scheduled at most this many months ahead. */
export const MAX_ADVANCE_MONTHS = 3

export const EARLIEST = 'earliest'
export const SPECIFIC = 'specific'

/** Minutes between fulfilment slots. */
export const SLOT_STEP_MINUTES = 15

const SLOT_STEP_MS = SLOT_STEP_MINUTES * 60000

const slotClocks = Array.from(
  { length: (24 * 60) / SLOT_STEP_MINUTES },
  (_, i) => moment.utc(0).add(i * SLOT_STEP_MINUTES, 'minutes'),
)

/** Fulfilment slots across the whole day, as venue wall-clock labels. */
export const TIME_SLOTS = slotClocks.map((clock) => clock.format('h:mm A'))

// The calendar checks every slot of every day it shows, so the 'h:mm A' label to
// 'HH:mm:ss' conversion is a lookup rather than a moment parse.
const CLOCK_BY_LABEL = TIME_SLOTS.reduce((byLabel, label, i) => {
  byLabel[label] = slotClocks[i].format('HH:mm:ss')
  return byLabel
}, {})

const clockOf = (time) =>
  CLOCK_BY_LABEL[time] || moment(time, 'h:mm A').format('HH:mm:ss')

const DAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

// The weekday of a YYYY-MM-DD date, 0 for Sunday.
const weekdayOf = (date) => new Date(`${date}T00:00:00Z`).getUTCDay()

// Opening hours can push the soonest open slot past the 3 days corporate searches.
const EARLIEST_SEARCH_DAYS = 14

const zoneOf = (timezone) => timezone || DEFAULT_CATERING_TIMEZONE

const slotMoment = ({ date, time }, timezone) =>
  moment.tz(`${date} ${time}`, 'YYYY-MM-DD h:mm A', zoneOf(timezone))

const addDays = (date, days) =>
  moment(date, 'YYYY-MM-DD').add(days, 'days').format('YYYY-MM-DD')

/** Today's date in the venue's timezone. */
export const todayIn = (timezone, now = Date.now()) =>
  moment.tz(now, zoneOf(timezone)).format('YYYY-MM-DD')

/** The last date an order can be scheduled for (inclusive). */
export const maxScheduleDate = (timezone, now = Date.now()) =>
  moment(todayIn(timezone, now), 'YYYY-MM-DD')
    .add(MAX_ADVANCE_MONTHS, 'months')
    .format('YYYY-MM-DD')

// isSlotAvailable runs once per slot with the same timezone and now, so keep the
// last answer.
let lastMaxDate = { key: null, date: null }
const cachedMaxScheduleDate = (timezone, now) => {
  const key = `${timezone}|${now}`
  if (lastMaxDate.key !== key) {
    lastMaxDate = { key, date: maxScheduleDate(timezone, now) }
  }
  return lastMaxDate.date
}

// openingHours is the venue doc's { day, start, end } entries, as HH:mm:ss
// strings. A day can have several entries (split hours, e.g. 00:00-01:30 and
// 11:00-23:59:59), and a slot is open if any of them covers it. start === end
// means closed. An end before the start closes after midnight, so the hours up
// to it also count on the next day. Without openingHours, every slot is open.
const entriesFor = (openingHours, weekday) =>
  openingHours.filter((o) => o.day === DAY_NAMES[weekday] && o.start !== o.end)

const closesAfterMidnight = (entry) => entry.end < entry.start

const isWithinOpeningHours = ({ date, time }, openingHours) => {
  if (!openingHours || !openingHours.length) return true
  const slotTime = clockOf(time)
  const weekday = weekdayOf(date)
  const openToday = entriesFor(openingHours, weekday).some(
    (entry) =>
      slotTime >= entry.start &&
      (closesAfterMidnight(entry) || slotTime < entry.end),
  )
  if (openToday) return true
  return entriesFor(openingHours, (weekday + 6) % 7).some(
    (entry) => closesAfterMidnight(entry) && slotTime < entry.end,
  )
}

/**
 * The slot instant nearest to `MIN_NOTICE_HOURS` from now. It can be up to half a
 * step under the notice period. Venue UTC offsets are whole multiples of the step,
 * so rounding the instant also rounds the venue wall clock.
 */
// `+ SLOT_STEP_MS / 2` rounds a tie (e.g. 7.5 minutes past) up to the later slot.
const noticeCutoff = (now) =>
  Math.floor(
    (now + MIN_NOTICE_HOURS * 3600000 + SLOT_STEP_MS / 2) / SLOT_STEP_MS,
  ) * SLOT_STEP_MS

/**
 * Whether a slot is no sooner than the notice cutoff (see noticeCutoff), no more
 * than 3 months ahead, and inside the venue's opening hours.
 */
export const isSlotAvailable = (
  slot,
  { timezone, openingHours, now = Date.now() } = {},
) => {
  if (!slot.date || !slot.time) return false
  if (slot.date > cachedMaxScheduleDate(timezone, now)) return false
  if (!isWithinOpeningHours(slot, openingHours)) return false
  return slotMoment(slot, timezone).valueOf() >= noticeCutoff(now)
}

/** The soonest available slot, or null when the venue has none in range. */
export const earliestSlot = (options = {}) => {
  const today = todayIn(options.timezone, options.now)
  for (let day = 0; day <= EARLIEST_SEARCH_DAYS; day++) {
    const date = addDays(today, day)
    const time = TIME_SLOTS.find((label) =>
      isSlotAvailable({ date, time: label }, options),
    )
    if (time) return { date, time }
  }
  return null
}

/**
 * The slot a request's date and time stand for right now. 'earliest' is the soonest
 * available slot. A specific slot that is no longer available (e.g. one saved a day
 * ago) also falls back to the soonest one, so an invalid time is never sent.
 */
export const resolveSlot = (dateTime, options = {}) => {
  if (dateTime && dateTime.mode === SPECIFIC && dateTime.date) {
    const slot = { date: dateTime.date, time: dateTime.time }
    if (isSlotAvailable(slot, options)) return slot
  }
  return earliestSlot(options)
}

/** "Tomorrow, 4:00 PM" / "Thu, 2 Oct, 4:00 PM" for a slot. */
export const slotLabel = (slot, { timezone, now = Date.now() } = {}) => {
  if (!slot) return 'No available times'
  const day =
    slot.date === addDays(todayIn(timezone, now), 1)
      ? 'Tomorrow'
      : moment(slot.date, 'YYYY-MM-DD').format('ddd, D MMM')
  return `${day}, ${slot.time}`
}

/** A slot as the RFC 3339 UTC instant the API takes as `catering.fulfillmentDate`. */
export const slotToIso = (slot, timezone) =>
  slot ? slotMoment(slot, timezone).toISOString() : null
